import { Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Sidebar from './Sidebar.jsx';
import Topbar from './Topbar.jsx';
import { useSession } from '../context/SessionContext.jsx';
import { admin } from '../api/client.js';
import PublicNotFound from '../pages/PublicNotFound.jsx';
import Landing from '../pages/Landing.jsx';

export default function Layout() {
  const { isLoading, error, collapsed, toggleCollapsed } = useSession();
  const [surface, setSurface] = useState(getSurface());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onResize = () => setSurface(getSurface());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close the mobile drawer on navigation, otherwise it covers the report
  // the user just picked.
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  if (isLoading) {
    return <div className="d-flex vh-100 align-items-center justify-content-center">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading</span>
      </div>
    </div>;
  }

  /* A 404 from /api/me means this hostname maps to no customer. That is a
     visitor on the wrong address, not a user with a broken account - and the
     old screen handed them our internal setup instructions. Vizora staff
     diagnose this at /healthz/routing, which names the host and says exactly
     what is missing. Other statuses only occur during setup, so they keep the
     technical detail. */
  // No session: the front door, with a Sign in button. See Landing.jsx.
  if (error?.status === 401) return <Landing />;
  if (error?.status === 404) return <PublicNotFound variant="unknown-host" />;
  if (error) return <AccountError error={error} />;

  return (
    <div className="portal-shell">
      <Sidebar
        surface={surface}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
        isOpen={drawerOpen}
        onNavigate={() => setDrawerOpen(false)}
      />
      {drawerOpen && (
        <button type="button" className="side-nav-scrim"
                aria-label="Close menu" onClick={() => setDrawerOpen(false)} />
      )}
      <div className="portal-main">
        <Topbar onOpenMenu={() => setDrawerOpen(true)} />
        <PreviewBar />
        <main className="portal-content"><Outlet /></main>
      </div>
    </div>
  );
}

/**
 * Shown while an administrator is viewing as another profile.
 *
 * Unmissable on purpose. Without it you are looking at someone else's menu
 * while believing it is your own, and the first thing you do is "fix" a
 * problem that was never there. It is also the only way back.
 */
function PreviewBar() {
  const { user } = useSession();
  const [leaving, setLeaving] = useState(false);
  if (!user?.isPreview) return null;

  const leave = async () => {
    setLeaving(true);
    try { await admin.endPreview(); } finally { window.location.href = '/admin/profiles'; }
  };

  return (
    <div className="preview-bar" role="status">
      <span>
        <i className="bi bi-eye me-2" aria-hidden="true" />
        Viewing as <strong>{user.profileName}</strong>
        <span className="preview-bar-note">
          {' '}— reports using row-level security will not open in this mode
        </span>
      </span>
      <button type="button" className="btn btn-sm btn-light"
              disabled={leaving} onClick={leave}>
        {leaving ? 'Leaving…' : 'Exit preview'}
      </button>
    </div>
  );
}

/**
 * A bare "Could not load your account" is useless while you are setting the
 * app up. Show the status and the server's own message, and for the failure
 * modes that actually happen during setup, say what to do about them.
 */
function AccountError({ error }) {
  const status = error.status;
  const message = error.body?.error ?? error.message;

  const hints = {
    404: [
      'The API could not map this hostname to a customer.',
      <>Add a row to <code>portal.CustomerDomains</code> for this host. For local
      development run <code>db/002_seed_dev.sql</code>, which inserts{' '}
      <code>localhost</code> and <code>127.0.0.1</code>.</>
    ],
    403: [
      'The customer record exists but is marked inactive.',
      <>Set <code>IsActive = 1</code> on the row in <code>portal.Customers</code>.</>
    ],
    500: [
      'The API hit an unhandled error.',
      <>Two causes account for nearly all of these. Check <code>/readyz</code>{' '}
      first: if it reports <code>schema_behind_code</code> a migration in{' '}
      <code>db/</code> has not been applied, which is far more likely than a
      connectivity problem. Otherwise the API cannot reach SQL — locally, blank{' '}
      <code>SQL_USER</code>/<code>SQL_PASSWORD</code> means it is trying Managed
      Identity, which needs <code>az login</code>.</>
    ],
    502: ['A dependency (Power BI or Microsoft Graph) rejected the request.', null]
  }[status];

  return (
    <div className="container py-5" style={{ maxWidth: 680 }}>
      <div className="card">
        <div className="card-body p-4">
          <h1 className="h5">Could not load your account</h1>
          <p className="text-muted mb-3">
            <code>GET /api/me</code> returned{' '}
            <span className="badge bg-secondary">{status ?? 'no response'}</span>
            {message && <> — {message}</>}
          </p>

          {hints && (
            <div className="alert alert-info mb-3">
              <div className="fw-semibold">{hints[0]}</div>
              {hints[1] && <div className="small mt-1">{hints[1]}</div>}
            </div>
          )}

          {!status && (
            <div className="alert alert-warning mb-3">
              <div className="fw-semibold">No response from the API.</div>
              <div className="small mt-1">
                Is it running on port 3000? The Vite dev server proxies{' '}
                <code>/api</code> and <code>/auth</code> to it.
              </div>
            </div>
          )}

          <button type="button" className="btn btn-sm btn-outline-secondary"
                  onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

function getSurface() {
  return window.innerWidth < 768 ? 'Mobile' : 'Desktop';
}
